import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { TrendingUp, Flame, Thermometer, Snowflake, UserX, Search, Target, Lightbulb, Users, BarChart3, Star, MessageCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";

const STAGE_CONFIG: Record<string, { label: string; icon: typeof Flame; color: string; badge: "default" | "secondary" | "destructive" | "outline" }> = {
  hot: { label: "Quente", icon: Flame, color: "text-destructive", badge: "destructive" },
  warm: { label: "Morno", icon: Thermometer, color: "text-orange-500", badge: "default" },
  cold: { label: "Frio", icon: Snowflake, color: "text-info", badge: "secondary" },
  lost: { label: "Perdido", icon: UserX, color: "text-muted-foreground", badge: "outline" },
};

const INSIGHT_ICONS: Record<string, typeof Target> = {
  opportunity: Target,
  action: MessageCircle,
  warning: AlertTriangle,
  metric: BarChart3,
};

const INSIGHT_COLORS: Record<string, string> = {
  opportunity: "border-green-500/30 bg-green-500/5",
  action: "border-primary/30 bg-primary/5",
  warning: "border-orange-500/30 bg-orange-500/5",
  metric: "border-muted-foreground/30 bg-muted/50",
};

const PIE_COLORS = ["hsl(var(--destructive))", "hsl(var(--warning))", "hsl(var(--info))", "hsl(var(--muted-foreground))"];

interface Lead {
  id: string;
  name: string;
  phone: string;
  group_name: string;
  classification: string;
  expertises: string[];
  engagement_score: number;
  lead_score: number;
  stage: string;
  days_since_activity: number;
  message_count_30d: number;
  recommendation: string;
  has_direct_conversation: boolean;
}

interface SalesData {
  leads: Lead[];
  pipeline: { hot: number; warm: number; cold: number; lost: number };
  insights: { type: string; title: string; description: string; priority: string }[];
  topExpertises: { name: string; count: number }[];
  total_leads: number;
  avg_score: number;
}

export default function SalesAssistant() {
  const [stageFilter, setStageFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = useQuery<SalesData>({
    queryKey: ["sales-insights"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("sales-insights");
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const filteredLeads = (data?.leads || []).filter(lead => {
    if (stageFilter !== "all" && lead.stage !== stageFilter) return false;
    if (searchTerm && !lead.name.toLowerCase().includes(searchTerm.toLowerCase()) && !lead.group_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const pipelineData = data?.pipeline
    ? [
        { name: "Quente", value: data.pipeline.hot },
        { name: "Morno", value: data.pipeline.warm },
        { name: "Frio", value: data.pipeline.cold },
        { name: "Perdido", value: data.pipeline.lost },
      ]
    : [];

  const pipelineKpis = [
    { label: "Quentes", value: data?.pipeline.hot || 0, icon: Flame, color: "text-destructive" },
    { label: "Mornos", value: data?.pipeline.warm || 0, icon: Thermometer, color: "text-orange-500" },
    { label: "Frios", value: data?.pipeline.cold || 0, icon: Snowflake, color: "text-info" },
    { label: "Score Médio", value: data?.avg_score || 0, icon: Star, color: "text-warning" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <TrendingUp className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold font-headline text-foreground">Sales Assistant</h1>
          <p className="text-sm text-muted-foreground">Pipeline de leads, scoring e recomendações de vendas</p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      ) : (
        <>
          {/* Pipeline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pipelineKpis.map((k, i) => (
              <motion.div key={k.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Card>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">{k.label}</span>
                      <k.icon className={`w-4 h-4 ${k.color}`} />
                    </div>
                    <div className="text-2xl font-bold font-headline text-foreground">{k.value}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <Tabs defaultValue="leads" className="space-y-4">
            <TabsList>
              <TabsTrigger value="leads">
                <Users className="w-4 h-4 mr-1.5" /> Leads ({data?.total_leads || 0})
              </TabsTrigger>
              <TabsTrigger value="insights">
                <Lightbulb className="w-4 h-4 mr-1.5" /> Insights
              </TabsTrigger>
              <TabsTrigger value="analytics">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Analytics
              </TabsTrigger>
            </TabsList>

            {/* Leads Tab */}
            <TabsContent value="leads" className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Buscar lead..."
                    className="pl-9"
                  />
                </div>
                <Select value={stageFilter} onValueChange={setStageFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="hot">🔥 Quentes</SelectItem>
                    <SelectItem value="warm">🌡️ Mornos</SelectItem>
                    <SelectItem value="cold">❄️ Frios</SelectItem>
                    <SelectItem value="lost">👻 Perdidos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredLeads.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Nenhum lead encontrado.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Lead</TableHead>
                          <TableHead>Grupo</TableHead>
                          <TableHead>Score</TableHead>
                          <TableHead>Estágio</TableHead>
                          <TableHead>Atividade</TableHead>
                          <TableHead>1:1</TableHead>
                          <TableHead>Recomendação</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLeads.slice(0, 50).map(lead => {
                          const stageCfg = STAGE_CONFIG[lead.stage] || STAGE_CONFIG.cold;
                          const StageIcon = stageCfg.icon;
                          return (
                            <TableRow key={lead.id}>
                              <TableCell>
                                <div>
                                  <span className="font-medium text-sm">{lead.name}</span>
                                  {lead.expertises.length > 0 && (
                                    <div className="flex gap-1 mt-0.5">
                                      {lead.expertises.slice(0, 2).map(e => (
                                        <Badge key={e} variant="outline" className="text-[10px] px-1">{e}</Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">{lead.group_name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Progress value={lead.lead_score} className="w-16 h-1.5" />
                                  <span className="text-xs font-mono font-medium">{lead.lead_score}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={stageCfg.badge} className="text-xs gap-1">
                                  <StageIcon className="w-3 h-3" /> {stageCfg.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {lead.days_since_activity === 0 ? "Hoje" :
                                 lead.days_since_activity === 1 ? "Ontem" :
                                 lead.days_since_activity < 999 ? `${lead.days_since_activity}d atrás` : "-"}
                              </TableCell>
                              <TableCell>
                                {lead.has_direct_conversation ? (
                                  <CheckCircle2 className="w-4 h-4 text-success" />
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[200px]">
                                <p className="text-xs text-muted-foreground truncate">{lead.recommendation}</p>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Insights Tab */}
            <TabsContent value="insights" className="space-y-4">
              {(data?.insights || []).length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Lightbulb className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Nenhum insight disponível no momento.</p>
                  </CardContent>
                </Card>
              ) : (
                (data?.insights || []).map((insight, i) => {
                  const Icon = INSIGHT_ICONS[insight.type] || Lightbulb;
                  const colorClass = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.metric;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }}
                    >
                      <Card className={`border ${colorClass}`}>
                        <CardContent className="pt-4 pb-3">
                          <div className="flex items-start gap-3">
                            <Icon className="w-5 h-5 mt-0.5 shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-sm text-foreground">{insight.title}</p>
                              <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
                            </div>
                            <Badge variant="outline" className="shrink-0 text-xs">{insight.priority}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })
              )}
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Pipeline Distribution */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Distribuição do Pipeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pipelineData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={pipelineData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={3}>
                            {pipelineData.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">Sem dados</div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 justify-center">
                      {pipelineData.map((d, i) => (
                        <div key={d.name} className="flex items-center gap-1.5 text-xs">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i] }} />
                          <span className="text-muted-foreground">{d.name}: {d.value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Expertises */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Top Expertises dos Leads</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(data?.topExpertises || []).length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={data!.topExpertises.slice(0, 8)} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" tick={{ fontSize: 10 }} className="text-muted-foreground" />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} className="text-muted-foreground" />
                          <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                          <Bar dataKey="count" name="Leads" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[250px] flex items-center justify-center text-sm text-muted-foreground">Sem dados de expertises</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
